import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from '@app/hooks/useAuth';
import { LoginPage } from '@app/pages/LoginPage';
import { ForbiddenPage } from '@app/pages/ForbiddenPage';
import { Layout } from '@app/pages/Layout';
import { OverviewPage } from '@app/pages/OverviewPage';
import { SubscribersPage } from '@app/pages/SubscribersPage';
import { SubscriberDetailPage } from '@app/pages/SubscriberDetailPage';
import { MessagesPage } from '@app/pages/MessagesPage';
import { PlansPage } from '@app/pages/PlansPage';
import { InvoicesPage } from '@app/pages/InvoicesPage';
import { TransactionsPage } from '@app/pages/TransactionsPage';
import { ApiKeysPage } from '@app/pages/ApiKeysPage';
import { AuditPage } from '@app/pages/AuditPage';
import { EnquiriesPage } from '@app/pages/EnquiriesPage';
import { UsTextingPage } from '@app/pages/UsTextingPage';
import { SystemPage } from '@app/pages/SystemPage';
import { DeliveryPage } from '@app/pages/DeliveryPage';
import { CampaignsPage } from '@app/pages/CampaignsPage';
import { TemplatesPage } from '@app/pages/TemplatesPage';
import { ContactsPage } from '@app/pages/ContactsPage';
import { CompliancePage } from '@app/pages/CompliancePage';
import { HLRPage } from '@app/pages/HLRPage';
import { OTPPage } from '@app/pages/OTPPage';
import { AIUsagePage } from '@app/pages/AIUsagePage';
import { ResellersPage } from '@app/pages/ResellersPage';
import { ResellerDetailPage } from '@app/pages/ResellerDetailPage';
import { ProtectedRoute } from '@app/routes/ProtectedRoute';

// Docs load on first visit (they bring the Markdown renderer with them)
const DocsIndexPage = lazy(() => import('@app/pages/docs/DocsIndexPage').then((m) => ({ default: m.DocsIndexPage })));
const DocPage = lazy(() => import('@app/pages/docs/DocPage').then((m) => ({ default: m.DocPage })));
const DocEditPage = lazy(() => import('@app/pages/docs/DocEditPage').then((m) => ({ default: m.DocEditPage })));

const lazyPage = (el: JSX.Element) => (
  <Suspense fallback={<div className="text-sm text-slate-500">Loading…</div>}>{el}</Suspense>
);

export function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forbidden" element={<ForbiddenPage />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="/subscribers" element={<SubscribersPage />} />
        <Route path="/subscribers/:id" element={<SubscriberDetailPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/enquiries" element={<EnquiriesPage />} />
        <Route path="/us-texting" element={<UsTextingPage />} />
        <Route path="/system" element={<SystemPage />} />
        <Route path="/delivery" element={<DeliveryPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/hlr" element={<HLRPage />} />
        <Route path="/otp" element={<OTPPage />} />
        <Route path="/ai-usage" element={<AIUsagePage />} />
        <Route path="/resellers" element={<ResellersPage />} />
        <Route path="/resellers/:id" element={<ResellerDetailPage />} />
        <Route path="/docs" element={lazyPage(<DocsIndexPage />)} />
        <Route path="/docs/c/:category" element={lazyPage(<DocsIndexPage />)} />
        <Route path="/docs/new" element={lazyPage(<DocEditPage />)} />
        <Route path="/docs/:slug" element={lazyPage(<DocPage />)} />
        <Route path="/docs/:slug/edit" element={lazyPage(<DocEditPage />)} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
